import type { NormalizedToolPart } from "./chat-tool-utils";
import type { ChatScope } from "./scope";

type TranslateFn = (key: string) => string;

// Replaces "[name]" with the member's name, or removes the connective
// preposition + placeholder when no name is available.
function withName(text: string, name?: string): string {
  if (name) return text.replace("[name]", name);
  return text
    .replace(/\s+(?:med|för|till)\s+\[name\]/g, "")
    .replace(/\[name\]s?\s+/g, "");
}

export interface QuickActionContext {
  memberName?: string;
  connectedAppIds: string[]; // uppercase toolkit slugs, e.g. ["GOOGLECALENDAR", "GMAIL"]
}

export function deriveQuickActions(
  visibleTools: NormalizedToolPart[],
  scope: ChatScope,
  _topicKey: string,
  context: QuickActionContext,
  t: TranslateFn,
): string[] {
  const { memberName, connectedAppIds } = context;
  const toolNames = new Set(visibleTools.map((tool) => tool.toolName));
  const result: string[] = [];

  function add(key: string) {
    const resolved = withName(t(`quickActions.${key}`), memberName);
    if (!result.includes(resolved) && result.length < 3) {
      result.push(resolved);
    }
  }

  function hasApp(id: string) {
    return connectedAppIds.includes(id);
  }

  // ─── Layer 1: react to tools that were just called ───────────────────────

  if (toolNames.has("intelligence_get_assessment")) {
    add("pdfActionPlan");
    add(hasApp("GOOGLECALENDAR") ? "bookCoachMeetingCalendar" : "compareWithTeam");
  }

  if (toolNames.has("intelligence_get_pulse_history")) {
    add("setGoal");
    add(hasApp("GOOGLECALENDAR") ? "bookFollowUpMeetingCalendar" : "bookFollowUpMeeting");
  }

  if (toolNames.has("intelligence_upsert_pulse_score")) {
    add("showUpdatedPulse");
    add("teamMoodNow");
  }

  if (toolNames.has("intelligence_get_team_pulse")) {
    add("diveIntoMember");
    add("comparePeriods");
  }

  if (
    toolNames.has("GOOGLECALENDAR_CREATE_EVENT") ||
    toolNames.has("GOOGLECALENDAR_QUICK_ADD")
  ) {
    add(hasApp("GMAIL") ? "sendMeetingAgenda" : "planNextSession");
  }

  if (
    toolNames.has("HUBSPOT_LIST_DEALS") ||
    toolNames.has("HUBSPOT_GET_DEAL") ||
    toolNames.has("HUBSPOT_LIST_CONTACTS")
  ) {
    add("showTeamPipeline");
    if (hasApp("GOOGLECALENDAR")) add("bookDealReview");
  }

  if (toolNames.has("intelligence_generate_action_plan_pdf")) {
    add(hasApp("GMAIL") ? "sendActionPlan" : "shareActionPlan");
  }

  if (
    toolNames.has("GMAIL_FETCH_EMAILS") ||
    toolNames.has("GMAIL_LIST_MESSAGES")
  ) {
    if (hasApp("GOOGLECALENDAR")) add("bookFollowUpMeetingWith");
  }

  // ─── Layer 2a: internal tools not yet used but relevant to scope ──────────

  if (result.length < 3) {
    if (scope.kind === "member") {
      if (
        !toolNames.has("intelligence_get_pulse_history") &&
        !toolNames.has("intelligence_upsert_pulse_score")
      ) {
        add("viewPulseHistory");
      }
      if (
        !toolNames.has("intelligence_get_assessment") &&
        !toolNames.has("intelligence_list_assessments")
      ) {
        add("fetchAssessments");
      }
    } else if (scope.kind === "team") {
      if (!toolNames.has("intelligence_get_team_pulse")) {
        add("showTeamPulse");
      }
      if (!toolNames.has("intelligence_list_assessments")) {
        add("fetchTeamAssessments");
      }
    } else {
      if (!toolNames.has("intelligence_list_teams")) {
        add("teamOverview");
      }
    }
  }

  // ─── Layer 2b: connected Composio apps not yet surfaced ──────────────────

  if (result.length < 3) {
    if (scope.kind === "member") {
      if (hasApp("GOOGLECALENDAR")) add("bookCoachMeetingCalendar");
      if (hasApp("HUBSPOT")) add("viewDealsHubSpot");
      if (hasApp("GMAIL")) add("sendSummaryEmail");
      if (hasApp("SLACK")) add("sendFeedbackSlack");
    } else if (scope.kind === "team") {
      if (hasApp("GOOGLECALENDAR")) add("scheduleTeamCheckIn");
      if (hasApp("HUBSPOT")) add("showTeamPipelineHubSpot");
    }
  }

  // ─── Layer 3: hard fallbacks ──────────────────────────────────────────────

  if (result.length < 2) {
    if (scope.kind === "member") {
      add("pdfActionPlan");
    } else if (scope.kind === "team") {
      add("compareSellers");
    } else {
      add("teamOverview");
    }
  }

  return result;
}
